import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import apiFetch from "../../apiFetch.js";
import { T, REL } from "../../theme.js";
import { usePeople } from "../../context/PeopleContext.jsx";
import { supabase } from "../../supabaseClient.js";
import { fetchOAuthUrl, openNativeOAuth, isNative } from "../../utils/nativeOAuth.js";
import { Av, Tog } from "../ui/index.jsx";
import { HAvatar } from "../home/HomeContent.jsx";
import {
  toLocalYMD, _CAL_MONTHS, WEEK_DATES, WEEK_DATE_NUMS, toTimeInput, fromTimeInput,
  TODAY_IDX, MONTH_DAYS, TODAY_DAY, CURRENT_MONTH_NAME, CAL_EVENTS,
  CAL_SOURCE_COLORS, CAL_TYPE_COLORS, GS_DAY_FULL, gsFmtH,
  HOME_TODAY, HOME_WEEK_DAYS, _sunday, _nowDate, crossProviderDedupe,
} from "../../utils/calendarUtils.js";

export function EventDetail({ev,setSelectedEvent,setEditingEvent,setCancelModal,onNav}){
  const {people:PEOPLE,blockNotes}=usePeople();
  if(!ev)return null;
  const p=ev.person?PEOPLE.find(x=>x.id===ev.person):null;
  const c=p?REL[p.type]?.color||T.partner:CAL_TYPE_COLORS[ev.type]||T.muted;
  const isOrbit=ev.source==="orbit";
  return(<div style={{position:"fixed",bottom:80,right:20,width:260,background:T.surface,borderRadius:16,border:`1.5px solid ${c}44`,boxShadow:`0 8px 32px rgba(30,16,8,.18)`,zIndex:600,animation:"fadeUp .15s ease",padding:16}}>
    <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:12}}>
      {p?<HAvatar p={p} size={36}/>:<div style={{width:36,height:36,borderRadius:10,background:c+"20",border:`1.5px solid ${c}44`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:14}}>{ev.source==="apple"?"📅":ev.source==="icloud"?"☁️":ev.source==="google"?"🗓️":ev.source==="ofw"?"👶":ev.source==="yahoo"?"📆":ev.source==="outlook"?"📧":ev.source==="caldav"?"📡":"◎"}</div>}
      <div style={{flex:1,minWidth:0}}>
        <p style={{fontSize:13,fontWeight:700,color:T.ink,lineHeight:1.2}}>{ev.label}</p>
        <p style={{fontSize:11,color:T.muted,marginTop:2}}>{GS_DAY_FULL[ev.day]}, {ev.allDay?(()=>{const sd=ev.date||"";const ed=ev.endDate&&ev.endDate>sd?ev.endDate:null;const dd=sd.slice(8)||"?";if(!ed)return dd;const endFmt=ed.slice(5,7)===sd.slice(5,7)?ed.slice(8):`${ed.slice(8)}/${ed.slice(5,7)}`;return`${dd} → ${endFmt}`;})():`${gsFmtH(ev.startH)}–${gsFmtH(ev.endH)}`}</p>
      </div>
      <button onClick={()=>setSelectedEvent(null)} style={{width:20,height:20,borderRadius:"50%",background:T.mist,border:`1px solid ${T.edge}`,fontSize:11,cursor:"pointer",color:T.muted,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>✕</button>
    </div>
    <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:10}}>
      <span style={{fontSize:11,padding:"2px 8px",borderRadius:9,background:c+"15",border:`1px solid ${c}30`,color:c,fontWeight:600,textTransform:"capitalize"}}>{ev.type}</span>
      <span style={{fontSize:11,padding:"2px 8px",borderRadius:9,background:T.mist,border:`1px solid ${T.edge}`,color:T.muted}}>{ev.source==="orbit"?"Orbit":ev.source==="apple"?"Apple Cal":ev.source==="google"?"Google Cal":ev.source==="icloud"?"iCloud":ev.source==="outlook"?"Outlook":ev.source==="yahoo"?"Yahoo Cal":ev.source==="caldav"?"CalDAV":ev.source==="ofw"?"Family Wiz":"Calendar"}</span>
      {ev.status==="free"&&<span style={{fontSize:11,padding:"2px 8px",borderRadius:9,background:T.sage+"18",border:`1px solid ${T.sage}44`,color:T.sage,fontWeight:600}}>🟢 Free</span>}
      {ev.status==="away"&&<span style={{fontSize:11,padding:"2px 8px",borderRadius:9,background:"#8B6F47"+"18",border:`1px solid ${"#8B6F47"}44`,color:"#8B6F47",fontWeight:600}}>✈ Away</span>}
      {ev.allDay&&<span style={{fontSize:11,padding:"2px 8px",borderRadius:9,background:T.mist,border:`1px solid ${T.edge}`,color:T.muted}}>📌 All day</span>}
      {blockNotes&&isOrbit&&<span style={{fontSize:11,padding:"2px 8px",borderRadius:9,background:T.muted+"15",border:`1px solid ${T.edgeMid}`,color:T.muted}}>🔒 Shared as: Busy</span>}
    </div>
    <div style={{display:"flex",gap:6}}>
      {ev.source!=="ofw"&&<button onClick={()=>{setEditingEvent({...ev});setSelectedEvent(null);}} style={{flex:1,padding:"7px",borderRadius:10,background:T.mist,border:`1px solid ${T.edge}`,color:T.bark,fontSize:12,fontWeight:600,cursor:"pointer"}}>✎ Edit</button>}
      {ev.source==="ofw"&&<p style={{flex:1,fontSize:11,color:T.muted,padding:"7px 0",fontStyle:"italic"}}>Read-only from Family Wizard</p>}
      {isOrbit&&ev.id?.startsWith("orbit-sched-")&&<button onClick={()=>{setCancelModal({ev});setSelectedEvent(null);}} style={{flex:1,padding:"7px",borderRadius:10,background:T.red+"12",border:`1px solid ${T.red}30`,color:T.red,fontSize:12,fontWeight:600,cursor:"pointer"}}>✕ Cancel</button>}
      {p&&<button onClick={()=>{setSelectedEvent(null);onNav("schedule",p.id);}} style={{flex:1,padding:"7px",borderRadius:10,background:c,border:"none",color:"white",fontSize:12,fontWeight:600,cursor:"pointer"}}>{p.name} →</button>}
    </div>
  </div>);
}

export function EventEditModal({ev,setEditingEvent,setCancelModal,activeWeekDates}){
  const {people:PEOPLE,setCalEvents,googleCalStatus}=usePeople();
  const [draft,setDraft]=useState(ev||{});
  const [saveToGoogle,setSaveToGoogle]=useState(false);
  const [googleSaving,setGoogleSaving]=useState(false);
  if(!ev)return null;
  const isOrbitEvent=ev.source==="orbit";
  const p=draft.person?PEOPLE.find(x=>x.id===draft.person):null;
  const c=p?REL[p.type]?.color||T.partner:CAL_TYPE_COLORS[draft.type]||T.partner;
  const canSave=draft.label.trim().length>0&&draft.startH<draft.endH;
  const typeOpts=Object.keys(CAL_TYPE_COLORS);
  const save=async()=>{
    if(!canSave) return;
    const saved={...draft,label:draft.label.trim()};
    setCalEvents(evs=>evs.map(e=>e.id===draft.id?saved:e));
    if(saveToGoogle&&googleCalStatus==="connected"){
      setGoogleSaving(true);
      try{
        const {data:{session}}=await supabase.auth.getSession();
        if(session?.access_token){
          const eventDate=saved.date?new Date(saved.date+"T12:00:00"):new Date(activeWeekDates[saved.day]);
          const startDate=new Date(eventDate);
          startDate.setHours(Math.floor(saved.startH),Math.round((saved.startH%1)*60),0,0);
          const endDate=new Date(eventDate);
          endDate.setHours(Math.floor(saved.endH),Math.round((saved.endH%1)*60),0,0);
          await apiFetch("/api/google/create-event",{
            method:"POST",
            headers:{Authorization:`Bearer ${session.access_token}`,"Content-Type":"application/json"},
            body:JSON.stringify({
              summary:saved.label,
              start:startDate.toISOString(),
              end:endDate.toISOString(),
              timeZone:Intl.DateTimeFormat().resolvedOptions().timeZone,
            }),
          });
        }
      }catch{}
      finally{setGoogleSaving(false);}
    }
    setEditingEvent(null);
  };
  const remove=()=>{
    if(ev.source==="orbit"&&ev.id?.startsWith("orbit-sched-")){
      setCancelModal({ev});setEditingEvent(null);return;
    }
    setCalEvents(evs=>evs.filter(e=>e.id!==draft.id));
    setEditingEvent(null);
  };
  return(<div style={{position:"fixed",inset:0,background:"rgba(30,16,8,0.45)",zIndex:700,display:"flex",alignItems:"center",justifyContent:"center",padding:20}} onClick={e=>e.target===e.currentTarget&&setEditingEvent(null)}>
    <div style={{width:"100%",maxWidth:440,background:T.surface,borderRadius:20,boxShadow:"0 12px 48px rgba(30,16,8,0.22)",animation:"popIn .2s ease",overflow:"hidden"}}>
      <div style={{padding:"18px 20px 14px",borderBottom:`1px solid ${T.edge}`,display:"flex",alignItems:"center",gap:10}}>
        <div style={{width:10,height:10,borderRadius:"50%",background:c,flexShrink:0}}/>
        <div style={{flex:1,minWidth:0}}>
          <h3 style={{fontSize:17,fontFamily:"'Crimson Pro',serif",fontWeight:600,color:T.ink,lineHeight:1.2}}>Edit event</h3>
          {(draft.date||draft.day!=null)&&<p style={{fontSize:11,color:T.muted,marginTop:1}}>{draft.date?(()=>{const d=new Date(draft.date+"T12:00:00");return`${GS_DAY_FULL[d.getDay()]}, ${_CAL_MONTHS[d.getMonth()]} ${d.getDate()}`;})():GS_DAY_FULL[draft.day]||""}{!draft.allDay&&draft.startH!=null?` · ${gsFmtH(draft.startH)}–${gsFmtH(draft.endH)}`:""}</p>}
        </div>
        <button onClick={()=>setEditingEvent(null)} style={{width:26,height:26,borderRadius:"50%",background:T.mist,border:"none",cursor:"pointer",color:T.muted,fontSize:13}}>✕</button>
      </div>
      <div style={{padding:"16px 20px 20px",display:"flex",flexDirection:"column",gap:14}}>
        <div>
          <label style={{fontSize:11,color:T.muted,textTransform:"uppercase",letterSpacing:"0.07em",display:"block",marginBottom:5}}>Title</label>
          <input autoFocus value={draft.label} onChange={e=>setDraft(d=>({...d,label:e.target.value}))}
            style={{width:"100%",padding:"9px 12px",borderRadius:10,border:`1.5px solid ${draft.label?c+"60":T.edge}`,background:T.mist,fontSize:13,color:T.ink,fontFamily:"'DM Sans',sans-serif",outline:"none",boxSizing:"border-box"}}/>
        </div>
        <div>
          <label style={{fontSize:11,color:T.muted,textTransform:"uppercase",letterSpacing:"0.07em",display:"block",marginBottom:5}}>Day</label>
          <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>
            {GS_DAY_FULL.map((day,i)=>(
              <button key={i} onClick={()=>setDraft(d=>({...d,day:i}))}
                style={{padding:"4px 10px",borderRadius:12,border:`1.5px solid ${draft.day===i?c+"60":T.edge}`,background:draft.day===i?c+"14":T.mist,color:draft.day===i?c:T.muted,fontSize:11,cursor:"pointer",fontWeight:draft.day===i?600:400}}>
                {day.slice(0,3)}
              </button>
            ))}
          </div>
        </div>
        <div style={{display:"flex",gap:10}}>
          <div style={{flex:1}}>
            <label style={{fontSize:11,color:T.muted,textTransform:"uppercase",letterSpacing:"0.07em",display:"block",marginBottom:5}}>Start</label>
            <input type="time" value={toTimeInput(draft.startH)} onChange={e=>setDraft(d=>({...d,startH:fromTimeInput(e.target.value)}))}
              style={{width:"100%",padding:"8px 10px",borderRadius:10,border:`1.5px solid ${T.edge}`,background:T.mist,fontSize:13,color:T.ink,outline:"none",boxSizing:"border-box",fontFamily:"'DM Sans',sans-serif"}}/>
          </div>
          <div style={{flex:1}}>
            <label style={{fontSize:11,color:T.muted,textTransform:"uppercase",letterSpacing:"0.07em",display:"block",marginBottom:5}}>End</label>
            <input type="time" value={toTimeInput(draft.endH)} onChange={e=>setDraft(d=>({...d,endH:fromTimeInput(e.target.value)}))}
              style={{width:"100%",padding:"8px 10px",borderRadius:10,border:`1.5px solid ${T.edge}`,background:T.mist,fontSize:13,color:T.ink,outline:"none",boxSizing:"border-box",fontFamily:"'DM Sans',sans-serif"}}/>
          </div>
        </div>
        {draft.startH>=draft.endH&&<p style={{fontSize:11,color:T.red,marginTop:-8}}>End time must be after start time</p>}
        <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:5}}>
          <label style={{fontSize:11,color:T.muted,textTransform:"uppercase",letterSpacing:"0.07em"}}>Mark as:</label>
          <select value={draft.status||"busy"} onChange={e=>setDraft(d=>({...d,status:e.target.value}))}
            style={{padding:"7px 20px",borderRadius:10,border:`1.5px solid ${T.edge}`,background:T.mist,fontSize:13,color:T.ink,fontFamily:"'DM Sans',sans-serif",outline:"none",cursor:"pointer",appearance:"auto"}}>
            <option value="free">Free</option>
            <option value="busy">Busy</option>
            <option value="away">Away</option>
          </select>
        </div>
        <div>
          <label style={{fontSize:11,color:T.muted,textTransform:"uppercase",letterSpacing:"0.07em",display:"block",marginBottom:5}}>Type</label>
          <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>
            {typeOpts.map(t=>(
              <button key={t} onClick={()=>setDraft(d=>({...d,type:t}))}
                style={{padding:"4px 10px",borderRadius:12,border:`1.5px solid ${draft.type===t?CAL_TYPE_COLORS[t]+"60":T.edge}`,background:draft.type===t?CAL_TYPE_COLORS[t]+"14":T.mist,color:draft.type===t?CAL_TYPE_COLORS[t]:T.muted,fontSize:11,cursor:"pointer",fontWeight:draft.type===t?600:400,textTransform:"capitalize"}}>
                {t}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label style={{fontSize:11,color:T.muted,textTransform:"uppercase",letterSpacing:"0.07em",display:"block",marginBottom:5}}>Person <span style={{textTransform:"none",letterSpacing:0,color:T.edgeMid}}>(optional)</span></label>
          <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>
            <button onClick={()=>setDraft(d=>({...d,person:null}))} style={{padding:"4px 10px",borderRadius:12,border:`1.5px solid ${!draft.person?T.bark:T.edge}`,background:!draft.person?T.bark:T.mist,color:!draft.person?"white":T.muted,fontSize:11,cursor:"pointer",fontWeight:!draft.person?600:400}}>None</button>
            {PEOPLE.map(person=>{const pc=REL[person.type]?.color||T.muted;const sel=draft.person===person.id;return(
              <button key={person.id} onClick={()=>setDraft(d=>({...d,person:sel?null:person.id}))}
                style={{padding:"4px 10px",borderRadius:12,border:`1.5px solid ${sel?pc:T.edge}`,background:sel?pc+"18":T.mist,color:sel?pc:T.muted,fontSize:11,cursor:"pointer",fontWeight:sel?600:400,display:"flex",alignItems:"center",gap:4}}>
                <span style={{fontSize:11}}>{REL[person.type]?.icon}</span>{person.name}
              </button>
            );})}
          </div>
        </div>
        {!isOrbitEvent&&(
          <p style={{fontSize:11,color:T.muted,fontStyle:"italic",padding:"4px 0"}}>Changes are saved in Orbit's view. To update the original event, edit it in your calendar app.</p>
        )}
        {googleCalStatus==="connected"&&ev.source!=="google"&&(
          <div onClick={()=>setSaveToGoogle(v=>!v)} style={{display:"flex",alignItems:"center",gap:10,padding:"9px 12px",borderRadius:10,background:saveToGoogle?"#1A73E810":T.mist,border:`1.5px solid ${saveToGoogle?"#1A73E860":T.edge}`,cursor:"pointer",transition:"all .13s"}}>
            <div style={{width:40,height:22,borderRadius:11,background:saveToGoogle?"#1A73E8":T.edge,position:"relative",flexShrink:0,transition:"background .2s"}}>
              <div style={{position:"absolute",top:2,left:saveToGoogle?20:2,width:18,height:18,borderRadius:"50%",background:"white",boxShadow:"0 1px 3px rgba(0,0,0,.25)",transition:"left .2s"}}/>
            </div>
            <div>
              <p style={{fontSize:12,fontWeight:600,color:saveToGoogle?"#1A73E8":T.muted}}>Also add to Google Calendar</p>
              <p style={{fontSize:11,color:T.muted,marginTop:1}}>Creates a new event in your primary calendar</p>
            </div>
          </div>
        )}
        <div style={{display:"flex",gap:8,paddingTop:4}}>
          <button onClick={remove} style={{padding:"9px 14px",borderRadius:11,background:"transparent",border:`1px solid ${T.edge}`,color:T.red,fontSize:12,cursor:"pointer",fontWeight:500}}>Delete</button>
          <button onClick={()=>setEditingEvent(null)} style={{flex:1,padding:11,borderRadius:11,background:"transparent",border:`1px solid ${T.edge}`,color:T.muted,fontSize:13,cursor:"pointer"}}>Cancel</button>
          <button onClick={save} disabled={!canSave||googleSaving} style={{flex:2,padding:11,borderRadius:11,background:canSave&&!googleSaving?T.bark:T.edgeMid,border:"none",color:"white",fontSize:13,fontWeight:600,cursor:canSave&&!googleSaving?"pointer":"default",transition:"background .15s"}}>
            {googleSaving?"Saving…":"Save changes"}
          </button>
        </div>
      </div>
    </div>
  </div>);
}

export function CancelModal({cancelModal,setCancelModal}){
  const {people:PEOPLE,setCalEvents}=usePeople();
  const [cancelMsg,setCancelMsg]=useState("");
  const [busy,setBusy]=useState(false);
  if(!cancelModal) return null;
  const {ev}=cancelModal;
  const p=ev.person?PEOPLE.find(x=>x.id===ev.person):null;
  const dateStr=ev.date?(()=>{const d=new Date(ev.date+"T12:00:00");return`${GS_DAY_FULL[d.getDay()]}, ${_CAL_MONTHS[d.getMonth()]} ${d.getDate()}, ${gsFmtH(ev.startH)}–${gsFmtH(ev.endH)}`;})():"";
  const doCancel=async()=>{
    setBusy(true);
    try{
      const reqId=ev.id.replace("orbit-sched-","");
      const {data:{session}}=await supabase.auth.getSession();
      if(session?.access_token) await apiFetch("/api/scheduling/cancel",{method:"PATCH",headers:{Authorization:`Bearer ${session.access_token}`,"Content-Type":"application/json"},body:JSON.stringify({request_id:reqId,cancellation_message:cancelMsg.trim()||null})});
    }catch(_){}
    setCalEvents(evs=>evs.filter(e=>e.id!==ev.id));
    setCancelModal(null);
  };
  return(<div style={{position:"fixed",inset:0,background:"rgba(30,16,8,0.45)",zIndex:750,display:"flex",alignItems:"center",justifyContent:"center",padding:20}} onClick={e=>e.target===e.currentTarget&&setCancelModal(null)}>
    <div style={{width:"100%",maxWidth:380,background:T.surface,borderRadius:20,boxShadow:"0 12px 48px rgba(30,16,8,0.22)",animation:"popIn .2s ease",overflow:"hidden"}}>
      <div style={{padding:"18px 20px 14px",borderBottom:`1px solid ${T.edge}`}}>
        <h3 style={{fontSize:17,fontFamily:"'Crimson Pro',serif",fontWeight:600,color:T.ink,marginBottom:2}}>Cancel event?</h3>
        <p style={{fontSize:12,color:T.muted}}>{ev.label}{dateStr?` · ${dateStr}`:""}</p>
      </div>
      <div style={{padding:"16px 20px 20px",display:"flex",flexDirection:"column",gap:12}}>
        <div>
          <label style={{fontSize:11,color:T.muted,textTransform:"uppercase",letterSpacing:"0.07em",display:"block",marginBottom:5}}>Message <span style={{textTransform:"none",letterSpacing:0,color:T.edgeMid}}>(optional)</span></label>
          <textarea value={cancelMsg} onChange={e=>setCancelMsg(e.target.value)} placeholder="Let them know why you're cancelling…" rows={3} style={{width:"100%",padding:"9px 12px",borderRadius:10,border:`1.5px solid ${T.edge}`,background:T.mist,fontSize:13,color:T.ink,fontFamily:"'DM Sans',sans-serif",outline:"none",resize:"vertical",boxSizing:"border-box"}}/>
        </div>
        {p&&<p style={{fontSize:11,color:T.muted,fontStyle:"italic"}}>A notification will be sent to {p.name}.</p>}
        <div style={{display:"flex",gap:8}}>
          <button onClick={()=>setCancelModal(null)} style={{flex:1,padding:11,borderRadius:11,background:"transparent",border:`1px solid ${T.edge}`,color:T.muted,fontSize:13,cursor:"pointer"}}>Keep event</button>
          <button onClick={doCancel} disabled={busy} style={{flex:2,padding:11,borderRadius:11,background:busy?T.edgeMid:T.red,border:"none",color:"white",fontSize:13,fontWeight:600,cursor:busy?"default":"pointer",transition:"background .15s"}}>{busy?"Cancelling…":"Confirm cancellation"}</button>
        </div>
      </div>
    </div>
  </div>);
}

export function CalendarContent({
  onNav,isMobile,initialDay}){
  const {people:PEOPLE,calEvents,setCalEvents,googleCalStatus,userId,setGoogleCalAvail,setGoogleCalStatus,fetchGoogleCalEvents,icloudCalStatus,fetchICloudCalEvents,outlookCalStatus,fetchOutlookCalEvents,ofwCalStatus,fetchOfwCalEvents,yahooCalStatus,fetchYahooCalEvents,caldavCalStatus,fetchCaldavCalEvents,fetchOrbitCalEvents,blockNotes,hideOffline,accounts}=usePeople();
  const [view,setView]=useState(initialDay!=null?"day":"week");
  const [dayIdx,setDayIdx]=useState(initialDay!=null?initialDay:HOME_TODAY);
  const [filter,setFilter]=useState("all"); // "all"|"orbit"|"apple"|"ofw"
  const [gcalSyncing,setGcalSyncing]=useState(false);
  const [selectedEvent,setSelectedEvent]=useState(null);
  const [editingEvent,setEditingEvent]=useState(null); // null | event object draft
  const [cancelModal,setCancelModal]=useState(null);   // null | {ev}
  const [nowH,setNowH]=useState(()=>{const n=new Date();return n.getHours()+n.getMinutes()/60;});
  useEffect(()=>{const id=setInterval(()=>{const n=new Date();setNowH(n.getHours()+n.getMinutes()/60);},60000);return()=>clearInterval(id);},[]);

  // Week / month navigation
  const [weekOffset,setWeekOffset]=useState(0);
  const [monthOffset,setMonthOffset]=useState(0);
  const [monthEvents,setMonthEvents]=useState([]);

  // Dynamic week dates derived from weekOffset
  const activeMonday=useMemo(()=>{const m=new Date(_sunday);m.setDate(_sunday.getDate()+weekOffset*7);return m;},[weekOffset]);
  const activeWeekDates=useMemo(()=>Array.from({length:7},(_,i)=>{const d=new Date(activeMonday);d.setDate(activeMonday.getDate()+i);return d;}),[activeMonday]);
  const activeWeekDateNums=useMemo(()=>activeWeekDates.map(d=>d.getDate()),[activeWeekDates]);
  const activeTodayIdx=weekOffset===0?HOME_TODAY:-1;
  const activeWeekOfStr=`${_CAL_MONTHS[activeMonday.getMonth()]} ${activeMonday.getDate()}`;
  const fmtActiveDate=(i)=>`${_CAL_MONTHS[activeWeekDates[i].getMonth()]} ${activeWeekDateNums[i]}`;
  const activeWeekRangeStr=activeWeekDates[0].getMonth()===activeWeekDates[6].getMonth()
    ?`${_CAL_MONTHS[activeWeekDates[0].getMonth()]} ${activeWeekDateNums[0]}–${activeWeekDateNums[6]}`
    :`${_CAL_MONTHS[activeWeekDates[0].getMonth()]} ${activeWeekDateNums[0]} – ${_CAL_MONTHS[activeWeekDates[6].getMonth()]} ${activeWeekDateNums[6]}`;

  // Re-fetch events when weekOffset changes (skip initial mount — handled by context effects)
  const prevWeekOffsetRef=useRef(null);

  // ── Swipe-to-navigate (week / day / month) ───────────────────────────────────
  const swipeTouchRef=useRef(null);
  const handleSwipeTouchStart=useCallback((e)=>{
    const t=e.touches[0];
    swipeTouchRef.current={x:t.clientX,y:t.clientY};
  },[]);
  const handleSwipeTouchEnd=useCallback((e)=>{
    if(!swipeTouchRef.current) return;
    const dx=e.changedTouches[0].clientX-swipeTouchRef.current.x;
    const dy=e.changedTouches[0].clientY-swipeTouchRef.current.y;
    swipeTouchRef.current=null;
    // Ignore if more vertical than horizontal, or under 50px threshold
    if(Math.abs(dy)>Math.abs(dx)||Math.abs(dx)<50) return;
    if(dx<0){
      // Swipe left → forward
      if(view==="month") setMonthOffset(m=>m+1);
      else if(view==="day"){if(dayIdx<6)setDayIdx(d=>d+1);else{setWeekOffset(w=>w+1);setDayIdx(0);}}
      else setWeekOffset(w=>w+1);
    } else {
      // Swipe right → back
      if(view==="month") setMonthOffset(m=>m-1);
      else if(view==="day"){if(dayIdx>0)setDayIdx(d=>d-1);else{setWeekOffset(w=>w-1);setDayIdx(6);}}
      else setWeekOffset(w=>w-1);
    }
  },[view,dayIdx,setDayIdx,setWeekOffset,setMonthOffset]);
  useEffect(()=>{
    if(prevWeekOffsetRef.current===null){prevWeekOffsetRef.current=weekOffset;return;}
    if(!userId) return;
    prevWeekOffsetRef.current=weekOffset;
    const from=toLocalYMD(activeMonday);
    // Preserve static orbit events but drop scheduling-derived ones (will refetch below)
    setCalEvents(prev=>prev.filter(e=>!(e.source==="orbit"&&e.id?.startsWith("orbit-sched-"))));
    (async()=>{
      const {data:{session}}=await supabase.auth.getSession();
      if(!session) return;
      const to=toLocalYMD(activeWeekDates[6]);
      await Promise.all([
        fetchGoogleCalEvents(session.access_token,from,to),
        fetchICloudCalEvents(session.access_token,from,to),
        fetchOutlookCalEvents(session.access_token,from,to),
        fetchYahooCalEvents(session.access_token,from,to),
        fetchCaldavCalEvents(session.access_token,from,to),
        fetchOfwCalEvents(session.access_token,from,to),
        fetchOrbitCalEvents(from,to),
      ]);
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[weekOffset,userId]);

  // Fetch month events when entering month view or navigating months
  useEffect(()=>{
    if(view!=="month"||!userId) return;
    const base=new Date(_nowDate.getFullYear(),_nowDate.getMonth()+monthOffset,1);
    const firstDow=base.getDay(); // 0=Sun aligns with Sun-start grid
    const firstVisible=new Date(base);firstVisible.setDate(1-firstDow);
    const lastVisible=new Date(firstVisible);lastVisible.setDate(firstVisible.getDate()+42);
    const from=toLocalYMD(firstVisible);
    const to=toLocalYMD(lastVisible);
    (async()=>{
      const {data:{session}}=await supabase.auth.getSession();
      if(!session) return;
      const tz=encodeURIComponent(Intl.DateTimeFormat().resolvedOptions().timeZone);
      const [gr,ir,or,ya,cd,fw]=await Promise.all([
        fetch(`/api/google/events?tz=${tz}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,{headers:{Authorization:`Bearer ${session.access_token}`}}).then(r=>r.ok?r.json():null).catch(()=>null),
        fetch(`/api/icloud/events?tz=${tz}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,{headers:{Authorization:`Bearer ${session.access_token}`}}).then(r=>r.ok?r.json():null).catch(()=>null),
        fetch(`/api/outlook/events?tz=${tz}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,{headers:{Authorization:`Bearer ${session.access_token}`}}).then(r=>r.ok?r.json():null).catch(()=>null),
        fetch(`/api/yahoo/events?tz=${tz}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,{headers:{Authorization:`Bearer ${session.access_token}`}}).then(r=>r.ok?r.json():null).catch(()=>null),
        fetch(`/api/caldav/events?tz=${tz}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,{headers:{Authorization:`Bearer ${session.access_token}`}}).then(r=>r.ok?r.json():null).catch(()=>null),
        fetch(`/api/ofw/events?tz=${tz}&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,{headers:{Authorization:`Bearer ${session.access_token}`}}).then(r=>r.ok?r.json():null).catch(()=>null),
      ]);
      // Fetch accepted scheduling requests for the visible month range
      let orbitSchedEvts=[];
      try{
        const{data:orbitSched}=await supabase
          .from("scheduling_requests")
          .select("*")
          .or(`from_user_id.eq.${userId},to_user_id.eq.${userId}`)
          .eq("status","accepted")
          .gte("proposed_date",from)
          .lte("proposed_date",to);
        if(orbitSched){
          orbitSchedEvts=orbitSched.map(req=>{
            const iAmSender=req.from_user_id===userId;
            const otherName=iAmSender?(req.to_display_name||"Someone"):(req.from_display_name||"Someone");
            const d=new Date(req.proposed_date+"T12:00:00");
            const jsDay=d.getDay();
            return{
              id:`orbit-sched-${req.id}`,
              source:"orbit",
              type:req.relationship_type||"date",
              day:jsDay, // 0=Sun…6=Sat
              date:req.proposed_date,
              startH:Number(req.proposed_start_h),
              endH:Number(req.proposed_end_h),
              label:otherName+(req.message?` · ${req.message}`:""),
              person:null,
            };
          });
        }
      }catch(_){}
      setMonthEvents([...(gr?.events||[]),...(ir?.events||[]),...(or?.events||[]),...(ya?.events||[]),...(cd?.events||[]),...(fw?.events||[]),...orbitSchedEvts]);
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[view,monthOffset,userId]);

  const CAL_HOUR_H=44;
  const CAL_START=7;
  const CAL_END=24;
  const CAL_HOURS=CAL_END-CAL_START;

  // Layout timed events side-by-side when they overlap.
  // Sort: same startH → longest first → label alphabetically.
  // Groups events into overlap clusters; within each cluster assigns columns.
  // Returns events with _col (0-based) and _totalCols added.
  function layoutDayEvents(evts){
    if(!evts.length) return [];
    const sorted=[...evts].sort((a,b)=>{
      if(a.startH!==b.startH) return a.startH-b.startH;
      const dA=a.endH-a.startH,dB=b.endH-b.startH;
      if(dA!==dB) return dB-dA;
      return (a.label||'').localeCompare(b.label||'');
    });
    const clusters=[];
    let cur=null;
    for(const ev of sorted){
      if(!cur||ev.startH>=cur.end){cur={events:[],end:-Infinity,cols:[]};clusters.push(cur);}
      let col=cur.cols.findIndex(endH=>endH<=ev.startH);
      if(col<0) col=cur.cols.length;
      cur.cols[col]=ev.endH;
      cur.end=Math.max(cur.end,ev.endH);
      cur.events.push({ev,col});
    }
    return clusters.flatMap(cl=>{
      const n=cl.cols.length;
      return cl.events.map(({ev,col})=>({...ev,_col:col,_totalCols:n}));
    });
  }

  const activeWeekDateSet=new Set(activeWeekDates.map(toLocalYMD));
  const _wkStart=toLocalYMD(activeWeekDates[0]);
  const _wkEnd=toLocalYMD(activeWeekDates[6]);

  // Generate all-day birthday events for connections within a date range
  const birthdayEventsForRange=useCallback((fromYMD,toYMD)=>{
    const evts=[];
    const fromYr=parseInt(fromYMD.slice(0,4));
    const toYr=parseInt(toYMD.slice(0,4));
    for(const p of PEOPLE){
      if(!p.birthday) continue;
      const bMM=p.birthday.slice(5,7);
      const bDD=p.birthday.slice(8,10);
      for(let yr=fromYr;yr<=toYr;yr++){
        const dateStr=`${yr}-${bMM}-${bDD}`;
        if(dateStr>=fromYMD&&dateStr<=toYMD){
          const d=new Date(dateStr+"T12:00:00");
          evts.push({id:`orbit-bday-${p.id}-${yr}`,source:"orbit",type:"birthday",allDay:true,label:`🎂 ${p.name}'s birthday`,date:dateStr,day:d.getDay(),person:p.id});
        }
      }
    }
    return evts;
  },[PEOPLE]);

  // Birthday events for the active week (merged into filtered below)
  const weekBirthdayEvts=useMemo(()=>birthdayEventsForRange(_wkStart,_wkEnd),[birthdayEventsForRange,_wkStart,_wkEnd]);

  // Remove cross-provider duplicates using two passes:
  //   1. Exact iCal UID + date match (synced events share the same UID)
  //   2. Fuzzy match by label + date + start/end time (catches events added
  //      independently to multiple calendars, meeting invites sent to multiple
  //      addresses, and events without UIDs)
  // In both passes the highest-priority provider copy wins.
  // Pre-pass: collect orbit-sched time slots; any non-orbit event on the same
  // date whose start/end is within 5 min of an orbit event is suppressed so
  // iCloud/Google entries that mirror an accepted orbit request don't duplicate.
  const _orbitSlots=calEvents.filter(e=>e.source==="orbit"&&e.id?.startsWith("orbit-sched-")&&!e.allDay);
  const _eventsForDedup=(filter==="all"?calEvents:calEvents.filter(e=>e.source===filter)).filter(e=>{
    if(!e.date) return true;
    const evEnd=e.endDate||e.date;
    if(!(e.date<=_wkEnd&&evEnd>=_wkStart)) return false;
    // Suppress non-orbit events that nearly overlap an accepted orbit slot
    if(e.source!=="orbit"&&!e.allDay&&_orbitSlots.some(o=>
      o.date===e.date&&Math.abs(o.startH-e.startH)<0.1&&Math.abs(o.endH-e.endH)<0.1
    )) return false;
    return true;
  });
  const filtered=crossProviderDedupe([..._eventsForDedup,...weekBirthdayEvts]);

  // Day view: single column with time grid
  const DayColumn=({d})=>{
    const allEvts=filtered.filter(e=>e.day===d);
    // Separate timed from all-day (only calendar all-day events go to banner row)
    const timedEvts=allEvts.filter(e=>!e.allDay);
    const laid=layoutDayEvents(timedEvts);
    const isToday=d===activeTodayIdx;
    return(<div style={{flex:1,position:"relative",minWidth:0}}>
      <div style={{height:CAL_HOUR_H*CAL_HOURS,position:"relative"}}>
        {/* Hour gridlines */}
        {Array.from({length:CAL_HOURS},(_,i)=>(
          <div key={i} style={{position:"absolute",top:i*CAL_HOUR_H,left:0,right:0,height:1,background:i===0?T.edge:T.edge+"80",zIndex:1}}/>
        ))}
        {/* Now line */}
        {isToday&&nowH>=CAL_START&&nowH<=CAL_END&&<div style={{position:"absolute",top:(nowH-CAL_START)*CAL_HOUR_H,left:0,right:0,height:2,background:T.red,zIndex:6,borderRadius:1}}><div style={{position:"absolute",left:-4,top:-3,width:8,height:8,borderRadius:"50%",background:T.red}}/></div>}
        {/* Timed Events — laid out side-by-side when overlapping */}
        {laid.map(ev=>{
          const top=(ev.startH-CAL_START)*CAL_HOUR_H;
          const height=(ev.endH-ev.startH)*CAL_HOUR_H-2;
          const c=ev.source==="orbit"?T.orbit:ev.person?REL[PEOPLE.find(p=>p.id===ev.person)?.type]?.color||CAL_TYPE_COLORS[ev.type]:CAL_TYPE_COLORS[ev.type]||T.muted;
          const isSel=selectedEvent?.id===ev.id;
          const isFreeEv=ev.status==="free";
          const isAway=ev.status==="away";
          // Use left+right instead of left+width so the event's right edge is
          // anchored directly to the containing block — avoids %-in-calc overflow.
          const gap=2;
          const colL=`calc(${(ev._col/ev._totalCols)*100}% + ${gap}px)`;
          const colR=`calc(${((ev._totalCols-ev._col-1)/ev._totalCols)*100}% + ${gap}px)`;
          return(<div key={ev.id} onClick={e=>{if(e.detail===2){if(ev.source!=="ofw"){setEditingEvent({...ev});setSelectedEvent(null);}}else{setSelectedEvent(isSel?null:ev);}}}
            style={{position:"absolute",top,left:colL,right:colR,height,borderRadius:8,
              background:isFreeEv?"transparent":c+"22",
              border:isFreeEv?`1px dashed ${isSel?c:c+"77"}`:`1px solid ${isSel?c:c+"55"}`,
              cursor:"pointer",padding:"3px 6px",overflow:"hidden",zIndex:5,
              opacity:isFreeEv?0.8:1,
              boxShadow:isSel?`0 2px 12px ${c}44`:"none",transition:"all .13s"}}>
            <p style={{fontSize:11,fontWeight:700,color:c,lineHeight:1.3,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
              {isFreeEv&&<span style={{opacity:0.7}}>🟢 </span>}{isAway&&<span style={{opacity:0.8}}>✈ </span>}{ev.label}
            </p>
            {height>24&&<p style={{fontSize:11,color:c+"AA",marginTop:1}}>{gsFmtH(ev.startH)}–{gsFmtH(ev.endH)}{isFreeEv?" · Free":isAway?" · Away":""}</p>}
            {ev.source!=="orbit"&&height>36&&<p style={{fontSize:11,color:c+"88",marginTop:1}}>{ev.source==="apple"?"📅":ev.source==="icloud"?"☁️":ev.source==="google"?"🗓️":ev.source==="ofw"?"👶":ev.source==="yahoo"?"📆":ev.source==="outlook"?"📧":ev.source==="caldav"?"📡":"◎"}</p>}
          </div>);
        })}
      </div>
    </div>);
  };

  const countBy=src=>calEvents.filter(e=>e.source===src).length;
  const filterPills=[
    {id:"all",   l:"All",    count:calEvents.length,   color:T.bark},
    {id:"orbit", l:"Orbit",  count:countBy("orbit"),   color:T.partner},
    ...(googleCalStatus==="connected"?[{id:"google",l:"Google Cal",count:countBy("google"),color:"#1A73E8"}]:[]),
    ...(icloudCalStatus==="connected"?[{id:"icloud",l:"iCloud",count:countBy("icloud"),color:"#1C7BE0"}]:[{id:"apple",l:"Apple Cal",count:countBy("apple"),color:"#1C7BE0"}]),
    ...(outlookCalStatus==="connected"?[{id:"outlook",l:"Outlook",count:countBy("outlook"),color:"#0078D4"}]:[]),
    ...(yahooCalStatus==="connected"?[{id:"yahoo",l:"Yahoo!",count:countBy("yahoo"),color:"#6001D2"}]:[]),
    ...(caldavCalStatus==="connected"?[{id:"caldav",l:"CalDAV",count:countBy("caldav"),color:"#5A7BAE"}]:[]),
    ...accounts.filter(a=>a.providerId!=="ofw"&&a.providerId!=="icloud"&&a.providerId!=="yahoo"&&a.providerId!=="caldav"&&a.providerId!=="google"&&a.providerId!=="outlook").map(a=>{const PROV_META={icloud:{label:"iCloud",color:"#1C7BE0"},google:{label:"Google Cal",color:"#1A73E8"},yahoo:{label:"Yahoo!",color:"#6001D2"},outlook:{label:"Outlook",color:"#0078D4"},caldav:{label:"CalDAV",color:"#5A7BAE"}};const p=PROV_META[a.providerId]||{label:a.providerId,color:T.muted};return{id:a.providerId,l:p.label,count:countBy(a.providerId),color:p.color};}),
    ...(ofwCalStatus==="connected"?[{id:"ofw",l:"OFW",count:countBy("ofw"),color:T.coparent}]:[]),
  ];

  return(<div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}} onTouchStart={handleSwipeTouchStart} onTouchEnd={handleSwipeTouchEnd}>

    {/* Header bar */}
    <div style={{padding:isMobile?"10px 18px 0":"12px 24px 0",flexShrink:0,background:T.surface,borderBottom:`1px solid ${T.edge}`}}>
      <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:10}}>
        <div>
          <h2 style={{fontSize:isMobile?20:24,fontFamily:"'Crimson Pro',serif",fontWeight:300,color:T.ink,lineHeight:1}}>Calendar</h2>
          <p style={{fontSize:11,color:T.muted,marginTop:2}}>
            {view==="month"
              ?`${_CAL_MONTHS[new Date(_nowDate.getFullYear(),_nowDate.getMonth()+monthOffset,1).getMonth()]} ${new Date(_nowDate.getFullYear(),_nowDate.getMonth()+monthOffset,1).getFullYear()}`
              :activeWeekRangeStr}
          </p>
        </div>
        <div style={{marginLeft:"auto",display:"flex",gap:6,alignItems:"center"}}>
          {/* Prev/next navigation */}
          {view!=="month"&&<div style={{display:"flex",gap:4}}>
            <button onClick={()=>{
              if(view==="day"){
                if(dayIdx>0){setDayIdx(d=>d-1);}
                else{setWeekOffset(w=>w-1);setDayIdx(6);}
              } else {setWeekOffset(w=>w-1);}
            }} style={{width:26,height:26,borderRadius:"50%",background:T.mist,border:`1px solid ${T.edge}`,cursor:"pointer",color:T.muted,fontSize:12}}>‹</button>
            {weekOffset!==0&&<button onClick={()=>{setWeekOffset(0);setDayIdx(HOME_TODAY);}} style={{padding:"0 8px",height:26,borderRadius:13,background:T.mist,border:`1px solid ${T.edge}`,cursor:"pointer",color:T.bark,fontSize:11,fontWeight:600}}>Today</button>}
            <button onClick={()=>{
              if(view==="day"){
                if(dayIdx<6){setDayIdx(d=>d+1);}
                else{setWeekOffset(w=>w+1);setDayIdx(0);}
              } else {setWeekOffset(w=>w+1);}
            }} style={{width:26,height:26,borderRadius:"50%",background:T.mist,border:`1px solid ${T.edge}`,cursor:"pointer",color:T.muted,fontSize:12}}>›</button>
          </div>}
          {view==="month"&&<div style={{display:"flex",gap:4}}>
            <button onClick={()=>setMonthOffset(m=>m-1)} style={{width:26,height:26,borderRadius:"50%",background:T.mist,border:`1px solid ${T.edge}`,cursor:"pointer",color:T.muted,fontSize:12}}>‹</button>
            {monthOffset!==0&&<button onClick={()=>setMonthOffset(0)} style={{padding:"0 8px",height:26,borderRadius:13,background:T.mist,border:`1px solid ${T.edge}`,cursor:"pointer",color:T.bark,fontSize:11,fontWeight:600}}>Today</button>}
            <button onClick={()=>setMonthOffset(m=>m+1)} style={{width:26,height:26,borderRadius:"50%",background:T.mist,border:`1px solid ${T.edge}`,cursor:"pointer",color:T.muted,fontSize:12}}>›</button>
          </div>}
          {/* View toggle */}
          <div style={{display:"flex",background:T.mist,borderRadius:9,padding:2,gap:1,border:`1px solid ${T.edge}`}}>
            {[{id:"month",l:"Month"},{id:"week",l:"Week"},{id:"day",l:"Day"}].map(v=>(
              <button key={v.id} onClick={()=>setView(v.id)} style={{padding:"4px 10px",borderRadius:7,border:"none",cursor:"pointer",background:view===v.id?T.surface:"transparent",color:view===v.id?T.ink:T.muted,fontSize:11,fontWeight:view===v.id?600:400,transition:"all .12s",boxShadow:view===v.id?"0 1px 3px rgba(0,0,0,.07)":"none"}}>{v.l}</button>
            ))}
          </div>
        </div>
      </div>

      {/* Source filter pills */}
      <div style={{display:"flex",gap:6,paddingBottom:10,flexWrap:"wrap"}}>
        {filterPills.map(f=>(
          <button key={f.id} onClick={()=>setFilter(f.id)} style={{display:"flex",alignItems:"center",gap:5,padding:"4px 10px",borderRadius:16,border:`1.5px solid ${filter===f.id?f.color:T.edge}`,background:filter===f.id?f.color+"12":T.mist,color:filter===f.id?f.color:T.muted,fontSize:11,fontWeight:filter===f.id?600:400,cursor:"pointer",flexShrink:0,transition:"all .13s"}}>
            {f.l}<span style={{fontSize:11,background:filter===f.id?f.color+"22":T.edge,borderRadius:8,padding:"0 5px",color:filter===f.id?f.color:T.muted,fontWeight:600}}>{f.count}</span>
          </button>
        ))}
      </div>

      {/* Google Calendar integration status */}
      <div style={{display:"flex",alignItems:"center",gap:8,paddingBottom:10}}>
        {googleCalStatus==="loading"&&(
          <div style={{display:"flex",alignItems:"center",gap:6,padding:"4px 10px",borderRadius:16,background:T.mist,border:`1px solid ${T.edge}`}}>
            <div style={{width:7,height:7,borderRadius:"50%",background:T.muted,animation:"pulse 1.5s infinite"}}/>
            <span style={{fontSize:11,color:T.muted}}>Syncing Google Calendar…</span>
          </div>
        )}
        {googleCalStatus==="connected"&&(
          <button onClick={async()=>{
            if(gcalSyncing) return;
            setGcalSyncing(true);
            try{await fetchGoogleCalEvents();}finally{setGcalSyncing(false);}
          }} disabled={gcalSyncing} style={{display:"flex",alignItems:"center",gap:6,padding:"4px 12px",borderRadius:16,background:T.mist,border:`1px solid ${T.edge}`,color:gcalSyncing?T.muted:"#1A73E8",fontSize:11,fontWeight:600,cursor:gcalSyncing?"default":"pointer",transition:"all .13s"}}>
            <span style={{fontSize:13}}>🔄</span>
            <span>{gcalSyncing?"Syncing…":"Sync now"}</span>
          </button>
        )}
        {googleCalStatus==="needs_reauth"&&userId&&(
          <div style={{display:"flex",alignItems:"center",gap:8,padding:"5px 12px",borderRadius:16,background:T.red+"10",border:`1px solid ${T.red}40`}}>
            <span style={{fontSize:11,color:T.red}}>Google session expired —</span>
            <button type="button" onClick={async()=>{const {data:{session}}=await supabase.auth.getSession();if(session?.access_token){const providerUrl=await fetchOAuthUrl('/api/google/auth',session.access_token,{native:isNative()});if(isNative()){try{await openNativeOAuth(providerUrl);}catch{}}else{window.location.href=providerUrl;}}}} style={{fontSize:11,color:T.red,fontWeight:700,background:"none",border:"none",cursor:"pointer",padding:0}}>Reconnect</button>
          </div>
        )}
        {(googleCalStatus===null||googleCalStatus==="error")&&userId&&(
          <button type="button" onClick={async()=>{const {data:{session}}=await supabase.auth.getSession();if(session?.access_token){const providerUrl=await fetchOAuthUrl('/api/google/auth',session.access_token,{native:isNative()});if(isNative()){try{await openNativeOAuth(providerUrl);}catch{}}else{window.location.href=providerUrl;}}}} style={{display:"inline-flex",alignItems:"center",gap:6,padding:"4px 12px",borderRadius:16,background:T.mist,border:`1px solid ${T.edge}`,color:T.muted,fontSize:11,cursor:"pointer",transition:"all .13s",fontFamily:"inherit"}}>
            <span style={{fontSize:13}}>📅</span>
            <span>Connect Google Calendar</span>
            {googleCalStatus==="error"&&<span style={{color:T.red,marginLeft:2}}>(retry)</span>}
          </button>
        )}
        {hideOffline&&<span title="Your availability is hidden after midnight. Change in Settings → Privacy." style={{display:"inline-flex",alignItems:"center",gap:5,padding:"4px 10px",borderRadius:16,background:T.mist,border:`1px solid ${T.edge}`,fontSize:11,color:T.muted,cursor:"help"}}>⏻ Offline after midnight</span>}
      </div>
    </div>

    {/* Calendar grid */}
    <div style={{flex:1,overflow:"hidden",display:"flex",flexDirection:"column"}}>
      {view==="week"&&(()=>{
        /* Single-grid approach: header rows AND time-grid rows share ONE CSS Grid,
           so every column track is computed once — no sub-pixel drift between sections.
           Day-header cells are position:sticky so they stay visible while scrolling. */
        const GTC="38px repeat(7,1fr)";
        const allDayEvts=filtered.filter(e=>e.allDay);
        const ROW_H=22;
        const weekDateStrs=activeWeekDates.map(toLocalYMD);
        // All-day layout
        let sorted=[],evRow={},maxRows=0;
        if(allDayEvts.length){
          sorted=[...allDayEvts].sort((a,b)=>{
            const sA=a.endDate&&a.endDate>a.date?(new Date(a.endDate)-new Date(a.date))/86400000:0;
            const sB=b.endDate&&b.endDate>b.date?(new Date(b.endDate)-new Date(b.date))/86400000:0;
            if(sB!==sA) return sB-sA; return a.date<b.date?-1:1;
          });
          const rowSlots={};
          for(const ev of sorted){
            const evEnd=ev.endDate||ev.date;
            const cols=[];
            for(let i=0;i<7;i++){if(ev.date<=weekDateStrs[i]&&weekDateStrs[i]<=evEnd)cols.push(i);}
            if(!cols.length) continue;
            let row=0;
            while(true){
              if(!rowSlots[row]) rowSlots[row]=new Set();
              if(!cols.some(c=>rowSlots[row].has(c))){cols.forEach(c=>rowSlots[row].add(c));evRow[ev.id]=row;break;}
              row++;
            }
          }
          maxRows=Object.keys(evRow).length?Math.max(...Object.values(evRow))+1:0;
        }
        const colW=100/7;
        // hour rows start after day-header row (1) + optional all-day row (1)
        const hourStartRow=maxRows>0?3:2;
        return(
          <div className="orbit-cal-timegrid" style={{
            flex:1,overflowY:"auto",
            display:"grid",
            gridTemplateColumns:GTC,
            alignContent:"start",
          }}>
            {/* ── Row 1: Day headers — sticky ── */}
            <div style={{position:"sticky",top:0,zIndex:20,background:T.panel,borderRight:`1px solid ${T.edge}`,borderBottom:`1px solid ${T.edge}`}}/>
            {HOME_WEEK_DAYS.map((d,i)=>{
              const isToday=i===activeTodayIdx;
              const evCount=filtered.filter(e=>e.day===i).length;
              return(<div key={d} style={{position:"sticky",top:0,zIndex:20,textAlign:"center",padding:"8px 4px",cursor:"pointer",borderRight:i<6?`1px solid ${T.edge}`:"none",borderBottom:`1px solid ${T.edge}`,background:isToday?"#FDF0E6":T.surface}} onClick={()=>{setDayIdx(i);setView("day");}}>
                <p style={{fontSize:11,fontWeight:700,color:isToday?T.partner:T.muted,textTransform:"uppercase",letterSpacing:"0.07em"}}>{d}</p>
                <p style={{fontSize:16,fontFamily:"'Crimson Pro',serif",fontWeight:isToday?700:300,color:isToday?T.partner:T.ink,lineHeight:1}}>{activeWeekDateNums[i]}</p>
                {evCount>0&&<div style={{display:"flex",justifyContent:"center",gap:2,marginTop:3}}>
                  {Array.from({length:Math.min(evCount,3)},(_,j)=>{
                    const ev=filtered.filter(e=>e.day===i)[j];
                    const c=ev.person?REL[PEOPLE.find(p=>p.id===ev.person)?.type]?.color||T.muted:CAL_TYPE_COLORS[ev.type]||T.muted;
                    return <div key={j} style={{width:5,height:5,borderRadius:"50%",background:c}}/>;
                  })}
                </div>}
              </div>);
            })}
            {/* ── Row 2: All-day banner (only when events exist) ── */}
            {maxRows>0&&<>
              <div style={{display:"flex",alignItems:"flex-start",justifyContent:"flex-end",paddingRight:4,paddingTop:4,borderRight:`1px solid ${T.edge}`,borderBottom:`1px solid ${T.edge}`,background:T.panel+"88"}}>
                <span style={{fontSize:11,color:T.muted,lineHeight:1,textTransform:"uppercase",letterSpacing:"0.04em"}}>All day</span>
              </div>
              {/* Events area spans all 7 day columns — same grid, so colW% is exact */}
              <div style={{gridColumn:"2/-1",position:"relative",height:maxRows*ROW_H+4,background:T.panel+"88",borderBottom:`1px solid ${T.edge}`,overflow:"hidden"}}>
                {/* Dividers: subtract 1px so they land on the same pixel as the border-right of day cells */}
                {[1,2,3,4,5,6].map(ci=>(
                  <div key={ci} style={{position:"absolute",top:0,bottom:0,left:`calc(${ci*colW}% - 1px)`,width:1,background:T.edge,pointerEvents:"none"}}/>
                ))}
                {sorted.map(ev=>{
                  const row=evRow[ev.id];
                  if(row===undefined) return null;
                  const evEnd=ev.endDate||ev.date;
                  let sc=-1,ec=-1;
                  for(let i=0;i<7;i++){if(ev.date<=weekDateStrs[i]&&weekDateStrs[i]<=evEnd){if(sc===-1)sc=i;ec=i;}}
                  if(sc===-1) return null;
                  const isActualStart=ev.date>=weekDateStrs[0];
                  const isActualEnd=evEnd<=weekDateStrs[6];
                  const c=ev.person?REL[PEOPLE.find(p=>p.id===ev.person)?.type]?.color||CAL_TYPE_COLORS[ev.type]:CAL_TYPE_COLORS[ev.type]||T.muted;
                  const isSel=selectedEvent?.id===ev.id;
                  const isFreeEv=ev.status==="free";
                  return(<div key={ev.id}
                    onClick={e=>{if(e.detail===2){if(ev.source!=="ofw"){setEditingEvent({...ev});setSelectedEvent(null);}}else{setSelectedEvent(isSel?null:ev);}}}
                    style={{position:"absolute",top:row*ROW_H+2,
                      left:`calc(${sc*colW}% + ${isActualStart?2:0}px)`,
                      width:`calc(${(ec-sc+1)*colW}% - ${isActualStart?2:0}px - ${isActualEnd?2:0}px)`,
                      height:ROW_H-2,
                      borderRadius:`${isActualStart?4:0}px ${isActualEnd?4:0}px ${isActualEnd?4:0}px ${isActualStart?4:0}px`,
                      background:isFreeEv?"transparent":c+(isSel?"33":"20"),
                      border:`1px solid ${isSel?c:c+"44"}`,
                      borderLeft:isActualStart?`1px solid ${isSel?c:c+"44"}`:"none",
                      borderRight:isActualEnd?`1px solid ${isSel?c:c+"44"}`:"none",
                      display:"flex",alignItems:"center",padding:"1px 4px",gap:2,cursor:"pointer",overflow:"hidden",
                      boxShadow:isSel?`0 1px 6px ${c}33`:"none"}}>
                    {!isActualStart&&<span style={{fontSize:10,color:c,flexShrink:0,opacity:0.7}}>◀</span>}
                    <span style={{fontSize:11,fontWeight:600,color:c,flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                      {isFreeEv&&"🟢 "}{ev.label}
                    </span>
                    {!isActualEnd&&<span style={{fontSize:10,color:c,flexShrink:0,opacity:0.7}}>▶</span>}
                    {isActualEnd&&<span style={{fontSize:11,color:c+"55",flexShrink:0}}>📌</span>}
                  </div>);
                })}
              </div>
            </>}
            {/* ── Hour labels: column 1, auto-placed after header rows ── */}
            {Array.from({length:CAL_HOURS},(_,i)=>(
              <div key={i} style={{gridColumn:1,height:CAL_HOUR_H,display:"flex",alignItems:"flex-start",paddingTop:3,paddingRight:5,justifyContent:"flex-end",background:T.panel,borderRight:`1px solid ${T.edge}`}}>
                <span style={{fontSize:11,color:T.muted,lineHeight:1}}>{gsFmtH(CAL_START+i)}</span>
              </div>
            ))}
            {/* ── Day columns: explicit gridColumn + gridRow, same grid tracks ── */}
            {HOME_WEEK_DAYS.map((_,i)=>(
              <div key={i} style={{
                gridColumn:i+2,
                gridRow:`${hourStartRow}/${hourStartRow+CAL_HOURS}`,
                borderRight:i<6?`1px solid ${T.edge}`:"none",
                position:"relative",
                height:CAL_HOURS*CAL_HOUR_H,
              }}>
                <DayColumn d={i}/>
              </div>
            ))}
          </div>
        );
      })()}

      {view==="day"&&(<>
        <div style={{display:"flex",alignItems:"center",padding:"10px 16px",borderBottom:`1px solid ${T.edge}`,flexShrink:0,background:T.surface,gap:10}}>
          <p style={{fontSize:14,fontWeight:600,color:dayIdx===activeTodayIdx?T.partner:T.ink,fontFamily:"'Crimson Pro',serif"}}>{GS_DAY_FULL[dayIdx]}</p>
          <p style={{fontSize:13,color:T.muted}}>{fmtActiveDate(dayIdx)}</p>
          {dayIdx===activeTodayIdx&&<span style={{fontSize:11,background:T.partner,color:"white",padding:"1px 7px",borderRadius:8,fontWeight:600}}>Today</span>}
          <span style={{fontSize:12,color:T.muted,marginLeft:"auto"}}>{filtered.filter(e=>e.day===dayIdx).length} events</span>
        </div>
        {/* All-day banners for day view */}
        {(()=>{
          const dayAllDay=filtered.filter(e=>e.day===dayIdx&&e.allDay);
          if(!dayAllDay.length) return null;
          return(<div style={{borderBottom:`1px solid ${T.edge}`,flexShrink:0,background:T.panel+"88",padding:"4px 8px 4px 46px",display:"flex",flexDirection:"column",gap:3}}>
            {dayAllDay.map(ev=>{
              const c=ev.person?REL[PEOPLE.find(p=>p.id===ev.person)?.type]?.color||CAL_TYPE_COLORS[ev.type]:CAL_TYPE_COLORS[ev.type]||T.muted;
              const isFreeEv=ev.status==="free";
              const isSel=selectedEvent?.id===ev.id;
              const hasEnd=ev.endDate&&ev.endDate>ev.date;
              return(<div key={ev.id} onClick={e=>{if(e.detail===2){if(ev.source!=="ofw"){setEditingEvent({...ev});setSelectedEvent(null);}}else{setSelectedEvent(isSel?null:ev);}}}
                style={{padding:"2px 8px",borderRadius:6,background:isFreeEv?"transparent":c+"20",
                  border:isFreeEv?`1px dashed ${c}77`:`1px solid ${isSel?c:c+"44"}`,
                  display:"flex",alignItems:"center",gap:6,cursor:"pointer",
                  boxShadow:isSel?`0 1px 6px ${c}33`:"none"}}>
                <span style={{fontSize:11,fontWeight:600,color:c,flex:1}}>{isFreeEv&&"🟢 "}{ev.label}</span>
                {hasEnd&&<span style={{fontSize:11,color:c+"99"}}>→ {ev.endDate.slice(5,7)===ev.date.slice(5,7)?ev.endDate.slice(8):`${ev.endDate.slice(8)}/${ev.endDate.slice(5,7)}`}</span>}
                <span style={{fontSize:11,color:c+"66"}}>📌 All day</span>
              </div>);
            })}
          </div>);
        })()}
        <div style={{flex:1,overflowY:"auto",display:"flex"}}>
          <div style={{width:38,flexShrink:0,background:T.panel,borderRight:`1px solid ${T.edge}`}}>
            {Array.from({length:CAL_HOURS},(_,i)=>(
              <div key={i} style={{height:CAL_HOUR_H,display:"flex",alignItems:"flex-start",paddingTop:3,paddingRight:5,justifyContent:"flex-end"}}>
                <span style={{fontSize:11,color:T.muted,lineHeight:1}}>{gsFmtH(CAL_START+i)}</span>
              </div>
            ))}
          </div>
          <div style={{flex:1}}>
            <DayColumn d={dayIdx}/>
          </div>
        </div>
      </>)}

      {view==="month"&&(()=>{
        const base=new Date(_nowDate.getFullYear(),_nowDate.getMonth()+monthOffset,1);
        const mYear=base.getFullYear(),mMonth=base.getMonth();
        const firstDow=base.getDay(); // 0=Sun aligns with Sun-start grid
        const firstVisible=new Date(mYear,mMonth,1-firstDow);
        const days=Array.from({length:42},(_,i)=>{const d=new Date(firstVisible);d.setDate(firstVisible.getDate()+i);return d;});
        const todayStr=`${_nowDate.getFullYear()}-${String(_nowDate.getMonth()+1).padStart(2,'0')}-${String(_nowDate.getDate()).padStart(2,'0')}`;
        const evByDate={};
        const _addEvToDate=(ev,dStr)=>{if(!evByDate[dStr])evByDate[dStr]=[];if(!evByDate[dStr].find(e=>e.id===ev.id))evByDate[dStr].push(ev);};
        const _expandEv=(ev)=>{
          _addEvToDate(ev,ev.date);
          if(ev.endDate&&ev.endDate>ev.date){const cur=new Date(ev.date+"T12:00:00");const end=new Date(ev.endDate+"T12:00:00");cur.setDate(cur.getDate()+1);while(cur<=end){_addEvToDate(ev,toLocalYMD(cur));cur.setDate(cur.getDate()+1);}}
        };
        const _mBase=new Date(_nowDate.getFullYear(),_nowDate.getMonth()+monthOffset,1);
        const _mFirstDow=_mBase.getDay();
        const _mFirstVis=new Date(_mBase);_mFirstVis.setDate(1-_mFirstDow);
        const _mLastVis=new Date(_mFirstVis);_mLastVis.setDate(_mFirstVis.getDate()+41);
        const _mFrom=toLocalYMD(_mFirstVis);const _mTo=toLocalYMD(_mLastVis);
        const monthBirthdayEvts=birthdayEventsForRange(_mFrom,_mTo);
        const allMonthEvs=crossProviderDedupe([...monthEvents,...calEvents.filter(ev=>ev.date&&!monthEvents.find(m=>m.id===ev.id)),...monthBirthdayEvts.filter(b=>!monthEvents.find(m=>m.id===b.id))]);
        for(const ev of allMonthEvs){_expandEv(ev);}
        return(<div style={{flex:1,overflow:"hidden",display:"flex",flexDirection:"column",padding:"0 2px 2px"}}>
          {/* Day-of-week header */}
          <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:1,padding:"4px 2px 3px",flexShrink:0,background:T.surface}}>
            {HOME_WEEK_DAYS.map(d=><div key={d} style={{textAlign:"center",fontSize:11,fontWeight:700,color:T.muted,textTransform:"uppercase",letterSpacing:"0.05em"}}>{d}</div>)}
          </div>
          {/* 6-week grid — fills remaining height, no scroll */}
          <div style={{flex:1,display:"grid",gridTemplateColumns:"repeat(7,1fr)",gridTemplateRows:"repeat(6,1fr)",gap:1}}>
            {days.map((d,i)=>{
              const dStr=`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
              const isToday=dStr===todayStr;
              const isCurMonth=d.getMonth()===mMonth;
              const evs=evByDate[dStr]||[];
              return(<div key={i} onClick={()=>{
                const monIdx=d.getDay(); // 0=Sun…6=Sat
                const diffDays=Math.round((d-_sunday)/86400000);
                const targetWeekOffset=Math.floor(diffDays/7);
                setWeekOffset(targetWeekOffset);
                setDayIdx(monIdx);
                setView("day");
              }} style={{padding:"2px 3px",borderRadius:5,border:`1px solid ${isToday?T.partner:T.edge+"80"}`,background:isToday?"#FDF0E6":isCurMonth?T.surface:T.mist,cursor:"pointer",overflow:"hidden",opacity:isCurMonth?1:.55,display:"flex",flexDirection:"column"}}>
                <div style={{display:"flex",justifyContent:"center",marginBottom:1}}>
                  <span style={{fontSize:11,fontFamily:"'Crimson Pro',serif",fontWeight:isToday?700:400,lineHeight:1.3,background:isToday?T.partner:"transparent",color:isToday?"white":isCurMonth?T.ink:T.muted,borderRadius:"50%",width:16,height:16,display:"flex",alignItems:"center",justifyContent:"center"}}>{d.getDate()}</span>
                </div>
                {evs.length>0&&<div style={{display:"flex",flexWrap:"wrap",gap:2,justifyContent:"center"}}>
                  {evs.slice(0,5).map((ev,j)=>{
                    const c=ev.person?REL[PEOPLE.find(p=>p.id===ev.person)?.type]?.color||T.muted:CAL_TYPE_COLORS[ev.type]||T.muted;
                    return(<div key={j} style={{width:5,height:5,borderRadius:"50%",background:c,flexShrink:0}}/>);
                  })}
                  {evs.length>5&&<div style={{width:5,height:5,borderRadius:"50%",background:T.muted,flexShrink:0}}/>}
                </div>}
              </div>);
            })}
          </div>
        </div>);
      })()}
    </div>

    {/* Event detail popover */}
    {selectedEvent&&<EventDetail key={selectedEvent.id} ev={selectedEvent} setSelectedEvent={setSelectedEvent} setEditingEvent={setEditingEvent} setCancelModal={setCancelModal} onNav={onNav}/>}
    {/* Event edit modal */}
    {editingEvent&&<EventEditModal key={editingEvent.id} ev={editingEvent} setEditingEvent={setEditingEvent} setCancelModal={setCancelModal} activeWeekDates={activeWeekDates}/>}
    {/* Cancel confirmation modal */}
    {cancelModal&&<CancelModal key={cancelModal.ev.id} cancelModal={cancelModal} setCancelModal={setCancelModal}/>}
  </div>);
}

// ── Shells ──────────────────────────────────────────────────────────────────
